<script setup>
import { ref, reactive } from 'vue';
import BadgeShape from "@/Shared/BadgeShape";
import {useForm} from "@inertiajs/inertia-vue3";
import {Inertia} from "@inertiajs/inertia";
import {useRecaptcha} from "@/ReCaptcha";
import {
    Dialog,
    DialogPanel,
    TransitionChild,
    TransitionRoot,
} from '@headlessui/vue';
import CandidateIcon from "@/Shared/SVG/CandidateIcon";
import { XMarkIcon } from '@heroicons/vue/24/solid';

let props = defineProps({
    position: Object,
    errors: Object
});

let position = props.position.data

let form = useForm({
    position_id: position.id,
    first_name: null,
    last_name: null,
    email: null,
    phone: null,
    linkedin_link: null,
    additional_link: null,
    'g-recaptcha-response': ''
})

let candidateSubmitSettings = reactive({
    candidateSubmitModalShow: false,
    submitted: false
})

useRecaptcha();

let attachmentInput = ref();
function submit() {
    form['g-recaptcha-response'] = event.target['g-recaptcha-response'].value
    grecaptcha.reset();
    if (attachmentInput.value) {
        form.attachment = attachmentInput.value.files[0];
    }
    Inertia.post('/candidates', form, {
        onSuccess: () => {
            candidateSubmitSettings.submitted = true
            candidateSubmitSettings.candidateSubmitModalShow = true
        }
    })
}
</script>

<template>
    <TransitionRoot as="template" class="z-50" :show="candidateSubmitSettings.candidateSubmitModalShow">
        <Dialog as="div" class="relative z-10">
            <TransitionChild
                as="template"
                enter="ease-out duration-300"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in duration-200"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div
                    class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                />
            </TransitionChild>

            <div class="fixed inset-0 z-10 overflow-y-auto">
                <div
                    class="flex min-h-full justify-center sm:p-4 text-center sm:items-center sm:p-0"
                >
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enter-to="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 sm:scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <DialogPanel
                            class="relative transform sm:rounded-2xl bg-white w-full sm:max-w-3xl transition-all"
                        >
                            <div class="bg-white bg-opacity-20 sm:bg-opacity-0 px-4 pt-6 sm:px-10">
                                <div class="text-center">
                                    <div v-if="candidateSubmitSettings.candidateSubmitModalShow">
                                        <div class="flex justify-end">
                                            <button @click.prevent="candidateSubmitSettings.candidateSubmitModalShow = false">
                                                <XMarkIcon class="text-gray-600 h-6 w-6"></XMarkIcon>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="flex flex-col m-5 sm:m-0 px-4 pt-5 pb-4 sm:px-10 sm:pt-0 sm:pb-10">
                                <div class="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
                                    <div class="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                                        <div class="overflow-hidden">
                                            <div>
                                                <CandidateIcon class="m-auto w-[240px]"/>
                                                <p class="text-2xl -mt-20 px-12 mb-2 text-wgn-purple-2">
                                                    Thank you!
                                                </p>
                                                <p class="text-sm text-black text-opacity-70 px-32 mb-8">
                                                    We’ve received your application, we will assess it and come back to you as soon as possible
                                                </p>
                                                <button @click.prevent="candidateSubmitSettings.candidateSubmitModalShow = false" class="px-8 py-4 text-white bg-wgn-purple-3 rounded-[40px] m-auto">
                                                    <span>Done</span>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
    <div class="w-full relative">
        <h2 class="text-white text-3xl font-bold mt-[71px] mb-[35px] sm:mb-[75px] text-center px-2">Careers</h2>
        <div class="mb-10 max-w-5xl m-auto">
            <div class="bg-white rounded-xl">
                <div class="p-10">
                    <h1 class="text-2xl text-wgn-purple-2">{{position.title}}</h1>
                    <div class="mt-2 flex justify-start space-x-4">
                        <span class="text-black text-opacity-70 text-sm">{{position.category.name}}</span>
                        <span class="text-black text-opacity-70 text-sm">{{position.country_readable}}</span>
                    </div>
                    <div class="mt-4 flex justify-start space-x-1.5">
                        <BadgeShape type="block" class="bg-wgn-black-1 text-white text-xs py-2 text-opacity-60 rounded-md">{{position.type_readable}}</BadgeShape>
                        <BadgeShape v-if="position.is_remote" type="block" class="bg-wgn-black-1 text-white text-xs py-2 text-opacity-60 rounded-md">Remote</BadgeShape>
                    </div>
                    <div class="mt-8 mt-10">
                        <div v-html="position.job_description"></div>
                    </div>
                </div>

                <div class="bg-wgn-purple-3 bg-opacity-[0.03]" v-if="!candidateSubmitSettings.submitted">
                    <div class="px-24 py-10">
                        <form @submit.prevent="submit">
                            <div class="grid grid-cols-6 gap-4 gap-y-6">
                                <div class="col-span-6 sm:col-span-3">
                                    <input type="text" id="first_name" v-model="form.first_name" placeholder="First name" class="p-3 mt-1 block w-full h-12 rounded border border-wgn-blue-2 border-opacity-20 focus:border-wgn-blue-1 focus:ring-wgn-blue-1 text-sm">
                                    <div v-if="errors.first_name" class="text-red-600 text-xs">
                                        {{ errors.first_name }}
                                    </div>
                                </div>
                                <div class="col-span-6 sm:col-span-3">
                                    <input type="text" id="last_name" v-model="form.last_name" placeholder="Last name" class="p-3 mt-1 block w-full h-12 rounded border border-wgn-blue-2 border-opacity-20 focus:border-wgn-blue-1 focus:ring-wgn-blue-1 text-sm">
                                    <div v-if="errors.last_name" class="text-red-600 text-xs">
                                        {{ errors.last_name }}
                                    </div>
                                </div>
                                <div class="col-span-6 sm:col-span-3">
                                    <input type="text" id="phone" v-model="form.phone" placeholder="Mobile number" class="p-3 mt-1 block w-full h-12 rounded border border-wgn-blue-2 border-opacity-20 focus:border-wgn-blue-1 focus:ring-wgn-blue-1 text-sm">
                                    <div v-if="errors.phone" class="text-red-600 text-xs">
                                        {{ errors.phone }}
                                    </div>
                                </div>
                                <div class="col-span-6 sm:col-span-3">
                                    <input type="text" id="email" v-model="form.email" placeholder="Email address" class="p-3 mt-1 block w-full h-12 rounded border border-wgn-blue-2 border-opacity-20 focus:border-wgn-blue-1 focus:ring-wgn-blue-1 text-sm">
                                    <div v-if="errors.email" class="text-red-600 text-xs">
                                        {{ errors.email }}
                                    </div>
                                </div>
                                <div class="col-span-6 sm:col-span-3">
                                    <input type="text" id="linkedin_link" v-model="form.linkedin_link" placeholder="LinkedIn link" class="p-3 mt-1 block w-full h-12 rounded border border-wgn-blue-2 border-opacity-20 focus:border-wgn-blue-1 focus:ring-wgn-blue-1 text-sm">
                                    <div v-if="errors.linkedin_link" class="text-red-600 text-xs">
                                        {{ errors.linkedin_link }}
                                    </div>
                                </div>
                                <div class="col-span-6 sm:col-span-3">
                                    <input type="text" id="additional_link" v-model="form.additional_link" placeholder="Additional links (portfolio, behance...)" class="p-3 mt-1 block w-full h-12 rounded border border-wgn-blue-2 border-opacity-20 focus:border-wgn-blue-1 focus:ring-wgn-blue-1 text-sm">
                                    <div v-if="errors.additional_link" class="text-red-600 text-xs">
                                        {{ errors.additional_link }}
                                    </div>
                                </div>
                                <div class="col-span-6 sm:col-span-6">
                                    <input type="file" ref="attachmentInput" id="attachment" placeholder="Upload CV & Cover letter" class="p-3 mt-1 block w-full h-12 rounded border border-wgn-blue-2 border-opacity-20 focus:border-wgn-blue-1 focus:ring-wgn-blue-1 text-sm">
                                    <div v-if="errors.attachment" class="text-red-600 text-xs">
                                        {{ errors.attachment }}
                                    </div>
                                </div>
                                <div class="col-span-6 sm:col-span-6">
                                    <div class="g-recaptcha" :data-sitekey="$page.props.recaptcha_site_key">
                                    </div>
                                    <div v-if="errors['g-recaptcha-response']" class="text-red-700">
                                        {{ errors['g-recaptcha-response'] }}
                                    </div>
                                </div>
                            </div>

                            <div class="mt-5 flex">
                                <button type="submit" class="px-8 py-4 text-white bg-wgn-purple-3 rounded-[40px] m-auto">Apply Now</button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
